import React, { useState, useEffect, useRef } from 'react';
import { Palette, Type, Upload, Save, X, Loader2, Sparkles } from 'lucide-react';
import Button from '../../components/Button';
import Input from '../../components/Input';
import ColorPicker from '../../components/ColorPicker';
import { useBrandSettings } from '../../hooks/useSupabase';
import { useAuth } from '../../contexts/AuthContext';
import { uploadBrandLogo, deleteBrandLogo } from '../../services/brandService';

const Brand: React.FC = () => {
  const { brandSettings, loading: loadingSettings, updateBrandSettings } = useBrandSettings();
  const { user, loading: authLoading } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [brandName, setBrandName] = useState('');
  const [tagline, setTagline] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#4F46E5');
  const [secondaryColor, setSecondaryColor] = useState('#10B981');
  const [accentColor, setAccentColor] = useState('#F59E0B');
  const [brandStyle, setBrandStyle] = useState<string | null>(null);
  const [brandDescription, setBrandDescription] = useState('');
  const [industry, setIndustry] = useState<string | null>(null);
  const [typographyStyle, setTypographyStyle] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Load existing brand settings
  useEffect(() => {
    if (brandSettings) {
      setBrandName(brandSettings.brand_name || '');
      setTagline(brandSettings.tagline || '');
      setPrimaryColor(brandSettings.primary_color);
      setSecondaryColor(brandSettings.secondary_color);
      setAccentColor(brandSettings.accent_color);
      setBrandStyle(brandSettings.brand_style);
      setBrandDescription(brandSettings.brand_description || '');
      setIndustry(brandSettings.industry);
      setTypographyStyle(brandSettings.typography_style);
      setLogoUrl(brandSettings.logo_url);
      setPreviewUrl(brandSettings.logo_url);
    }
  }, [brandSettings]);

  const processLogoFile = (file?: File) => {
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Please upload PNG, JPG, or SVG files only.' });
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File size must be less than 10MB.' });
      return;
    }

    setMessage(null);
    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    processLogoFile(e.target.files?.[0]);
  };

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    }

    if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    processLogoFile(e.dataTransfer.files?.[0]);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setPreviewUrl(null);
    setLogoUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      let finalLogoUrl = logoUrl;

      // Upload new logo if selected
      if (logoFile) {
        setUploadingLogo(true);
        const result = await uploadBrandLogo(logoFile);
        
        if (result.error) {
          throw new Error(result.error);
        }

        // Delete old logo if exists
        if (logoUrl && logoUrl !== result.url) {
          try {
            await deleteBrandLogo(logoUrl);
          } catch (err) {
            console.error('Failed to delete old logo:', err);
          }
        }

        finalLogoUrl = result.url;
        setUploadingLogo(false);
      }

      // If logo was removed and no new file selected
      if (!logoFile && !previewUrl && logoUrl) {
        try {
          await deleteBrandLogo(logoUrl);
        } catch (err) {
          console.error('Failed to delete logo:', err);
        }
        finalLogoUrl = null;
      }

      // Update brand settings
      await updateBrandSettings({
        brand_name: brandName || null,
        tagline: tagline || null,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        accent_color: accentColor,
        brand_style: brandStyle || null,
        brand_description: brandDescription || null,
        industry: industry || null,
        typography_style: typographyStyle || null,
        logo_url: finalLogoUrl
      });

      setLogoUrl(finalLogoUrl);
      setLogoFile(null);
      setMessage({ type: 'success', text: 'Brand settings saved successfully!' });
      
      // Log to console as per user preference
      console.log('Brand settings saved:', {
        brand_name: brandName,
        tagline,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        accent_color: accentColor,
        brand_style: brandStyle,
        brand_description: brandDescription,
        industry,
        typography_style: typographyStyle,
        logo_url: finalLogoUrl
      });
    } catch (error: any) {
      console.error('Error saving brand settings:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save brand settings' });
    } finally {
      setSaving(false);
      setUploadingLogo(false);
    }
  };

  if (authLoading || loadingSettings) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-purple" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-h2 font-heading text-text-dark mb-2">
          Brand Settings
        </h1>
        <p className="text-body text-body-grey">
          Customize your brand identity for consistent mockups
        </p>
      </div>

      <div className="space-y-8">
        {/* Brand Information */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-text-dark mb-6 flex items-center">
            <Type className="w-5 h-5 mr-2 text-brand-purple" />
            Brand Information
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Brand Name
              </label>
              <Input
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="Enter your brand name"
              />
            </div>
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Tagline
              </label>
              <Input
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                placeholder="Enter your brand tagline"
              />
            </div>
          </div>
        </div>

        {/* Brand Identity */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-text-dark mb-6 flex items-center">
            <Sparkles className="w-5 h-5 mr-2 text-brand-purple" />
            Brand Identity
          </h2>
          <div className="space-y-6">
            {/* Brand Style */}
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Brand Style
              </label>
              <p className="text-sm text-body-grey mb-3">Select the aesthetic that best represents your brand</p>
              <div className="flex flex-wrap gap-2">
                {['luxury', 'minimalist', 'playful', 'bold', 'corporate', 'organic', 'vintage', 'modern'].map((style) => (
                  <button
                    key={style}
                    onClick={() => setBrandStyle(brandStyle === style ? null : style)}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                      brandStyle === style
                        ? 'bg-brand-purple text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {style.charAt(0).toUpperCase() + style.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Industry */}
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Industry
              </label>
              <select
                value={industry || ''}
                onChange={(e) => setIndustry(e.target.value || null)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-text-dark bg-white focus:outline-none focus:ring-2 focus:ring-brand-purple focus:border-transparent"
              >
                <option value="">Select your industry</option>
                <option value="fashion">Fashion</option>
                <option value="technology">Technology</option>
                <option value="food-beverage">Food & Beverage</option>
                <option value="health-wellness">Health & Wellness</option>
                <option value="finance">Finance</option>
                <option value="education">Education</option>
                <option value="entertainment">Entertainment</option>
                <option value="home-living">Home & Living</option>
                <option value="sports-fitness">Sports & Fitness</option>
                <option value="beauty-cosmetics">Beauty & Cosmetics</option>
                <option value="travel">Travel</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Typography Style */}
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Typography Style
              </label>
              <p className="text-sm text-body-grey mb-3">Choose a typography direction for text on mockups</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'sans-serif-clean', label: 'Sans-Serif Clean' },
                  { value: 'serif-elegant', label: 'Serif Elegant' },
                  { value: 'bold-display', label: 'Bold Display' },
                  { value: 'handwritten', label: 'Handwritten' },
                  { value: 'monospace', label: 'Monospace' },
                ].map((typo) => (
                  <button
                    key={typo.value}
                    onClick={() => setTypographyStyle(typographyStyle === typo.value ? null : typo.value)}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                      typographyStyle === typo.value
                        ? 'bg-brand-purple text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {typo.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Brand Description */}
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Brand Description
              </label>
              <textarea
                value={brandDescription}
                onChange={(e) => {
                  if (e.target.value.length <= 300) {
                    setBrandDescription(e.target.value);
                  }
                }}
                placeholder="Describe your brand's personality, values, and aesthetic..."
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-text-dark bg-white focus:outline-none focus:ring-2 focus:ring-brand-purple focus:border-transparent resize-none"
              />
              <p className="text-xs text-body-grey mt-1 text-right">
                {brandDescription.length}/300
              </p>
            </div>
          </div>
        </div>

        {/* Brand Colors */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-text-dark mb-6 flex items-center">
            <Palette className="w-5 h-5 mr-2 text-brand-purple" />
            Brand Colors
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Primary Color
              </label>
              <ColorPicker
                color={primaryColor}
                onChange={setPrimaryColor}
                label=""
              />
              <div className="mt-2 h-20 rounded-lg" style={{ backgroundColor: primaryColor }}></div>
            </div>
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Secondary Color
              </label>
              <ColorPicker
                color={secondaryColor}
                onChange={setSecondaryColor}
                label=""
              />
              <div className="mt-2 h-20 rounded-lg" style={{ backgroundColor: secondaryColor }}></div>
            </div>
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Accent Color
              </label>
              <ColorPicker
                color={accentColor}
                onChange={setAccentColor}
                label=""
              />
              <div className="mt-2 h-20 rounded-lg" style={{ backgroundColor: accentColor }}></div>
            </div>
          </div>
        </div>

        {/* Brand Logo */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-text-dark mb-6 flex items-center">
            <Upload className="w-5 h-5 mr-2 text-brand-purple" />
            Brand Logo
          </h2>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/svg+xml"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {previewUrl ? (
            <div className="relative">
              <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                <img 
                  src={previewUrl} 
                  alt="Brand logo preview" 
                  className="max-h-32 mx-auto object-contain"
                />
              </div>
              <button
                onClick={handleRemoveLogo}
                className="absolute top-2 right-2 p-1 bg-red-100 rounded-full hover:bg-red-200 transition-colors"
              >
                <X className="w-5 h-5 text-red-600" />
              </button>
              <p className="text-sm text-gray-600 mt-2 text-center">
                {logoFile ? 'New logo selected' : 'Current brand logo'}
              </p>
            </div>
          ) : (
            <div 
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                dragActive
                  ? 'border-brand-purple bg-brand-purple/5'
                  : 'border-gray-300 hover:border-brand-purple'
              }`}
            >
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">Upload your brand logo</p>
              <p className="text-sm text-gray-500 mb-2">PNG, JPG or SVG. Max 10MB</p>
              <p className="text-xs text-gray-400 mb-4">Drag and drop your logo here, or choose a file</p>
              <Button variant="secondary" size="small" onClick={() => {
                fileInputRef.current?.click();
              }}>
                Choose File
              </Button>
            </div>
          )}
        </div>

        {/* Message Display */}
        {message && (
          <div className={`p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            variant="primary" 
            size="large"
            onClick={handleSave}
            disabled={saving || uploadingLogo}
          >
            {saving || uploadingLogo ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {uploadingLogo ? 'Uploading Logo...' : 'Saving...'}
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Save Brand Settings
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Brand;
